import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { debounce } from '@mui/material/utils';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { SwitchField } from '@openx/components-core/lib/SwitchField/SwitchField.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import {
  type DealFormData,
  type DealFormDataWithTargeting,
  dealFormField,
  type RboConfig,
  type RboConfigField,
  rboConfigField,
} from '@openx/types/deal.ts';
import { rboConfigTactic } from '@openx/types/rbo.ts';
import { type UseWatchName, useWatchManyDebounce } from '@openx/utils/hooks/useWatchDebounce.ts';
import { noop } from '@openx/utils/noop.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import findKey from 'lodash/findKey';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';
import { useLocation } from 'react-router';
import { GroupDealRboAlert } from '../../components/DealGroup/GroupDealRboAlert.tsx';
import { ResultsByOpenXReadonly } from '../../components/ResultsByOpenX/ResultsByOpenXReadonly.tsx';
import { useRboFields } from '../../hooks/useRboFields.ts';
import { initRboConfig } from '../utils/constants.ts';
import { getRboLabels, getRboPlaceholders } from '../utils/dealFormUtils.ts';

const autocompleteFields: RboConfigField[] = [
  rboConfigField.TACTIC,
  rboConfigField.TARGET,
  rboConfigField.MODEL_GOAL,
  rboConfigField.CHANNEL,
  rboConfigField.TACTIC,
];
const textFields: RboConfigField[] = [
  rboConfigField.DROP_PERCENT,
  rboConfigField.HOURLY_TARGET,
  rboConfigField.THRESHOLD,
  rboConfigField.CUSTOM_SCORE_NAME,
];

type Props = {
  isEdit: boolean;
};

interface CustomProps {
  onChange: (event: {
    target: { name: string; value: string };
    preventDefault(): void;
    isDefaultPrevented(): boolean;
  }) => void;
  name: string;
}

type NumericFormatCustomProps = CustomProps & NumericFormatProps & InputBaseComponentProps;
const NumericFormatCustom = forwardRef<HTMLInputElement, NumericFormatCustomProps>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={({ value }, { event }) =>
        onChange({
          preventDefault: event?.preventDefault || noop,
          isDefaultPrevented: event?.isDefaultPrevented || (() => true),
          target: { ...event?.target, value, name: props.name },
        })
      }
      thousandSeparator
      valueIsNumericString
    />
  );
});

export const ResultsByOpenX = ({ isEdit }: Props): ReactNode => {
  const { t } = useTranslation();
  const locationState = useLocation().state;

  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const { rboUser, rboAdmin } = useMemo(
    () => ({
      rboAdmin: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_RBO_CONFIG_ADMIN_WRITE)),
      rboUser: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.DEAL_RBO_CONFIG_WRITE)),
    }),
    [isAllowed]
  );

  const rboLabels = useMemo(() => getRboLabels(t), [t]);
  const rboPlaceholders = useMemo(() => getRboPlaceholders(t), [t]);
  const {
    setValue,
    getValues,
    formState: { errors, defaultValues },
    trigger,
  } = useFormContext<DealFormDataWithTargeting>();

  const initialRboConfigForToggleRef = useRef<RboConfig | null | undefined>(undefined);

  useEffect(() => {
    if (initialRboConfigForToggleRef.current !== undefined) {
      return;
    }
    const snapshot = getValues(dealFormField.RBO_CONFIG) ?? defaultValues?.rbo_config;
    if (snapshot == null) {
      return;
    }
    initialRboConfigForToggleRef.current = structuredClone(snapshot);
  }, [defaultValues?.rbo_config, getValues]);

  const observableRboConfig: RboConfig = useWatch({
    name: dealFormField.RBO_CONFIG,
  });

  const observableModelGoal = useWatch({
    name: `${dealFormField.RBO_CONFIG}.${rboConfigField.MODEL_GOAL}`,
  });

  const names: UseWatchName<DealFormData> = useMemo(
    () => (isEdit ? [dealFormField.DEAL_ID] : [dealFormField.CUSTOM_DEAL_ID, dealFormField.EXTERNAL_DEAL_ID]),
    [isEdit]
  );
  const observableDealIds = useWatchManyDebounce({
    name: names,
  });

  const [hasRboInitially] = useState(!!observableRboConfig);
  const [resultsByOpenXChecked, setResultsByOpenXChecked] = useState(hasRboInitially);

  const group =
    useWatch<DealFormDataWithTargeting, typeof dealFormField.METADATA_GROUP_DEAL>({
      name: dealFormField.METADATA_GROUP_DEAL,
    }) || [];

  const currentDealId = observableDealIds.find(dealId => !!dealId) ?? '';
  const dealGroupLoading = !!currentDealId && !rboUser && group.length === 0;

  const { groupDealWithRbo, groupHasOtherDealWithRbo } = useMemo(() => {
    const groupDealWithRbo = group.find(({ rboConfig }) => !!rboConfig);

    return {
      groupDealWithRbo,
      groupHasOtherDealWithRbo: !!groupDealWithRbo && !hasRboInitially,
    };
  }, [group, hasRboInitially]);

  const isSwitchDisabled = useMemo(
    () => dealGroupLoading || groupHasOtherDealWithRbo,
    [dealGroupLoading, groupHasOtherDealWithRbo]
  );

  const shouldDisableGoalField = useMemo(() => {
    if (!hasRboInitially || !isEdit) {
      return false;
    }

    return !!observableRboConfig?.[rboConfigField.MODEL_GOAL];
  }, [hasRboInitially, isEdit, observableRboConfig?.[rboConfigField.MODEL_GOAL]]);

  const rboFields = useRboFields({ readonly: false });

  const handleClearField = useCallback(
    (field: RboConfigField) => {
      setValue(`${dealFormField.RBO_CONFIG}.${field}`, null, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const debouncedValidation = useMemo(
    () =>
      debounce((field: RboConfigField) => {
        return trigger(`${dealFormField.RBO_CONFIG}.${field}`);
      }),
    [trigger]
  );

  const onSwitchChange = useCallback(() => {
    setResultsByOpenXChecked(checked => {
      if (checked) {
        setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue(
          dealFormField.RBO_CONFIG,
          initialRboConfigForToggleRef.current ?? defaultValues?.[dealFormField.RBO_CONFIG] ?? { ...initRboConfig },
          { shouldDirty: true, shouldValidate: true }
        );
      }
      trigger(dealFormField.METADATA);
      return !checked;
    });
  }, [defaultValues, setValue, trigger]);

  const { tactic, threshold } = observableRboConfig ?? {};
  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    if (tactic === rboConfigTactic.DYNAMIC) {
      trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.THRESHOLD}`);
    }

    if (tactic === rboConfigTactic.FIXED) {
      trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.HOURLY_TARGET}`);
    }

    trigger(`${dealFormField.RBO_CONFIG}.${rboConfigField.CUSTOM_SCORE_NAME}`);
  }, [trigger, tactic, threshold]);

  useEffect(() => {
    if (groupHasOtherDealWithRbo) {
      setResultsByOpenXChecked(false);
      setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [setValue, groupHasOtherDealWithRbo]);

  useEffect(() => {
    if (locationState?.useAsTemplate) {
      setResultsByOpenXChecked(false);
      setValue(dealFormField.RBO_CONFIG, null, { shouldDirty: true, shouldValidate: true });
    }
  }, [locationState?.useAsTemplate, setValue]);

  useEffect(() => {
    if (!observableRboConfig) {
      setResultsByOpenXChecked(false);
    }
  }, [observableRboConfig]);

  // Clear value from Channel field when it is hidden
  useEffect(() => {
    const modelGoal = observableRboConfig?.[rboConfigField.MODEL_GOAL];

    if (modelGoal === 'conversion') {
      setValue(`${dealFormField.RBO_CONFIG}.${rboConfigField.CHANNEL}`, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [observableRboConfig?.[rboConfigField.MODEL_GOAL], setValue]);

  if (!rboUser) {
    return;
  }

  const { rbo_config: rboErrors = {} } = errors;

  return (
    <FormSection
      divider
      title={t('Engagement Optimization')}
      description={t('Once active, the feature may take up to 48 hours to converge to optimal deal volume & threshold')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
      data-test="results-by-openx"
    >
      <SwitchField
        label={t('Enable Engagement Optimization')}
        type="checkbox"
        checked={resultsByOpenXChecked || groupHasOtherDealWithRbo}
        onChange={onSwitchChange}
        data-test="toggle-results-by-openx"
        disabled={isSwitchDisabled}
      />
      {groupHasOtherDealWithRbo && groupDealWithRbo && (
        <>
          <GroupDealRboAlert key={groupDealWithRbo.id} deal={groupDealWithRbo} sx={{ marginBottom: 1, marginTop: 2 }} />
          <ResultsByOpenXReadonly rboConfig={groupDealWithRbo.rboConfig} />
        </>
      )}
      {resultsByOpenXChecked && (
        <Box
          data-test="results-by-openx"
          sx={{
            mt: 3,
          }}
        >
          {rboFields.map(({ field, options = {} }) => {
            const dataTestField = field.replaceAll('_', '-');

            // Hide Channel field if Goal is Conversion
            if (field === rboConfigField.CHANNEL && observableModelGoal === 'conversion') {
              return null;
            }

            if (autocompleteFields.includes(field)) {
              const isGoalField = field === rboConfigField.MODEL_GOAL;

              return (
                <Box
                  key={field}
                  sx={{
                    mb: 1.5,
                  }}
                >
                  <RHFAutoComplete
                    name={`${dealFormField.RBO_CONFIG}.${field}`}
                    textFieldProps={{
                      label: rboLabels[field],
                      placeholder: rboPlaceholders[field],
                    }}
                    options={Object.keys(options).toSorted()}
                    getOptionLabel={option => options[option] ?? option}
                    renderOptions={{
                      dataTest: `${dataTestField}-option`,
                    }}
                    onInputChange={
                      rboAdmin
                        ? (_, value) => {
                            const optionValue = findKey(options, optionLabel => optionLabel === value) ?? value;
                            setValue(`${dealFormField.RBO_CONFIG}.${field}`, value === '' ? null : optionValue, {
                              shouldDirty: true,
                            });
                            debouncedValidation(field);
                          }
                        : undefined
                    }
                    data-test={`rbo-${dataTestField}-input`}
                    fullWidth
                    freeSolo={rboAdmin}
                    disabled={isGoalField && shouldDisableGoalField}
                  />
                </Box>
              );
            }

            if (textFields.includes(field)) {
              return (
                <FormControl
                  key={field}
                  data-test={`rbo-${dataTestField}-input`}
                  margin="dense"
                  sx={{ mb: 1.5, mt: 0 }}
                  fullWidth
                >
                  <FormLabel error={!!rboErrors[field]}>{`${rboLabels[field]}`}</FormLabel>
                  <RHFTextField
                    name={`${dealFormField.RBO_CONFIG}.${field}`}
                    placeholder={rboPlaceholders[field]}
                    slotProps={{
                      input: {
                        endAdornment: observableRboConfig?.[field] ? (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleClearField(field)} size="small">
                              <Close />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                        inputComponent: field === rboConfigField.HOURLY_TARGET ? NumericFormatCustom : undefined,
                      },
                    }}
                    valueOverride={(observableRboConfig?.[field] ?? '') as string}
                    onChange={(value, event) => {
                      event.preventDefault();
                      setValue(`${dealFormField.RBO_CONFIG}.${field}`, value === '' ? null : value, {
                        shouldDirty: true,
                      });
                      debouncedValidation(field);
                    }}
                    variant="outlined"
                    fullWidth
                  />
                </FormControl>
              );
            }

            return null;
          })}
        </Box>
      )}
    </FormSection>
  );
};
